import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import * as React from 'react';
import { classNames } from "../../utils/helpers";
import { StarcOverrideContext } from "../starc";
import s from './Actionable.module.css';
export const Actionable = React.forwardRef(({ children, href, onClick, type, disabled, insetFocus, as, fullWidth, className, attributes, tabIndex, title, }, ref) => {
    const overrides = React.useContext(StarcOverrideContext);
    const rootClassNames = classNames(s.root, className, insetFocus && s['--inset'], fullWidth && s['--full-width']);
    const rootAttributes = Object.assign({}, attributes);
    const clickHandler = onClick || (attributes === null || attributes === void 0 ? void 0 : attributes.onClick);
    const keyDownHandler = attributes === null || attributes === void 0 ? void 0 : attributes.onKeyDown;
    const isLink = Boolean(href || (attributes === null || attributes === void 0 ? void 0 : attributes.href));
    const isButton = Boolean(clickHandler || type);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let TagName;
    if (isLink) {
        if (disabled) {
            rootAttributes['aria-disabled'] = true;
            rootAttributes.role = 'link';
            delete rootAttributes.href;
        }
        else {
            if (href) {
                rootAttributes.href = href;
                rootAttributes['tabIndex'] = tabIndex || 0;
            }
            rootAttributes['aria-label'] = title;
        }
        TagName = as || overrides.link || 'a';
        delete rootAttributes.disabled;
    }
    else if (isButton && (!as || as === 'button')) {
        TagName = 'button';
        rootAttributes.type = type || (attributes === null || attributes === void 0 ? void 0 : attributes.type) || 'button';
        rootAttributes.disabled = disabled || (attributes === null || attributes === void 0 ? void 0 : attributes.disabled);
        rootAttributes.title = title;
        rootAttributes.role = 'button';
    }
    else {
        const focusHandler = (attributes === null || attributes === void 0 ? void 0 : attributes.onFocus) || (attributes === null || attributes === void 0 ? void 0 : attributes.onBlur);
        const isFocusable = as === 'label';
        const simulateButton = !isFocusable || clickHandler || focusHandler;
        TagName = as || 'button';
        rootAttributes.role = simulateButton ? 'button' : undefined;
        rootAttributes.tabIndex = simulateButton ? tabIndex || 0 : undefined;
        rootAttributes['aria-label'] = title;
        rootAttributes.disabled = disabled || (attributes === null || attributes === void 0 ? void 0 : attributes.disabled);
    }
    const handlePress = event => {
        if (clickHandler && !disabled)
            clickHandler(event);
    };
    const handleClick = (event) => handlePress(event);
    const handleOnKeyDown = (event) => {
        const simulatingButton = rootAttributes.role === 'button';
        if (!simulatingButton || isLink)
            return;
        if (keyDownHandler)
            keyDownHandler(event);
    };
    rootAttributes['data-testid'] = `${rootAttributes['data-testid'] ? rootAttributes['data-testid'] + '-' : ''}st-actionable`;
    return (_jsx(TagName, Object.assign({}, rootAttributes, { ref: ref, className: rootClassNames, onClick: handleClick, onKeyDown: handleOnKeyDown, children: children })));
});
Actionable.displayName = 'Actionable';
